import React, { useEffect, useMemo } from "react";
import {
    Box,
    Button,
    Dialog,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { History } from "lucide-react";
import { Cancel } from "@mui/icons-material";
import { Column, useCommonReducer } from "../../../redux/slice/commonSlice";
import { getProductPricingHistory, useProductReducer } from "../../../redux/slice/product/createProductSlice";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";
import CustomCurrencyField from "../../common/CustomCurrencyField";
import { useModalStyles } from "../../common/useModalStyles";

interface ProductItemPriceSKUHistoryProps {
    open: boolean;
    onClose: () => void;
}

const ProductItemSKULevelPriceHistoryPopup: React.FC<ProductItemPriceSKUHistoryProps> = ({
    open,
    onClose,
}) => {
    const dispatch = useAppDispatch();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const {
        tokens,
        paperSx,
        headerSx,
        iconBoxSx,
        bodySx,
        footerSx,
        closeIconSx,
        cancelBtnSx,
        isDark,
    } = useModalStyles();

    const { productPricingHistory, productObject } = useProductReducer();
    const { macInfoList } = useCommonReducer();

    const modalTableSx = {
        width: "100%",
        border: `1px solid ${tokens.border}`,
        borderRadius: "12px",
        overflow: "hidden",
        backgroundColor: isDark ? "rgba(255,255,255,0.02)" : tokens.cardBg,
        "& .MuiTableContainer-root": { borderRadius: 0, boxShadow: "none", background: "transparent" },
        "& .MuiTable-root": { background: "transparent !important" },
        "& .MuiTableCell-head": {
            background: `${isDark ? "rgba(255,255,255,0.03)" : tokens.cardBg} !important`,
            borderBottom: `1px solid ${tokens.border}`,
            color: `${tokens.textSecondary} !important`,
            fontWeight: 600,
            fontSize: "0.78rem",
            height: 40,
            py: 0,
            px: 2,
            whiteSpace: "nowrap",
        },
        "& .MuiTableCell-body": {
            borderBottom: `1px solid ${tokens.border}`,
            color: tokens.textPrimary,
            fontSize: "0.85rem",
            py: 1.25,
            px: 2,
        },
        "& .sku-group-header": {
            background: `${isDark ? "rgba(255,255,255,0.05)" : "rgba(15,23,42,0.04)"} !important`,
            color: `${tokens.textSecondary} !important`,
            fontWeight: 600,
            fontSize: "0.78rem",
            textAlign: "center",
            borderBottom: `1px solid ${tokens.border}`,
            py: 1,
        },
        "& .sku-cell": {
            fontWeight: 600,
        },
        "& .MuiTableCell-body .MuiInputBase-input, & .MuiTableCell-body .MuiTypography-root": {
            color: `${tokens.textPrimary} !important`,
            fontSize: "0.85rem",
        },
        "& .MuiTableRow-root:last-child .MuiTableCell-body": {
            borderBottom: "none",
        },
    };

    const columnsPriceItem: Column[] = useMemo(() => {
        return [
            { id: "sku", label: "SKU" },
            { id: "size", label: "Size" },
            { id: "units", label: "# of Units" },
            { id: "listPrice", label: "List Price", align: "right" },
            { id: "allowableUnitPrice", label: "Allowable", align: "right" },
            { id: "customPrice", label: "Custom Price", align: "right" },
            { id: "priceType", label: "File Type" },
            { id: "effectiveDate", label: "Effective Date", align: "right" },
            { id: "inactiveDate", label: "Inactive Date", align: "right" },
            { id: "auditTimestamp", label: "Last Updated Time", minWidth: 100 },
        ];
    }, []);

    useEffect(() => {
        if (open) {
            dispatch(getProductPricingHistory({ productId: productObject.productId }));
        }
    }, [open, dispatch, productObject.productId]);

    const groupedRows = useMemo(() => {
        const groupedData: Record<string, any[]> = {};

        productPricingHistory?.forEach((obj) => {
            const effectiveDate = obj.effectiveDate || "No Effective Date";
            const inactiveDate = obj.inactiveDate || "No Inactive Date";

            const groupKey = `${effectiveDate}-${inactiveDate}`;

            const modifiedObj = {
                ...obj,
                sku: obj.productItem?.sku,
                size: obj.productItem?.size,
                units: obj.productItem?.units,
                listPrice: <CustomCurrencyField externalValue={obj?.listPrice} displayType="text" />,
                customPrice: <CustomCurrencyField externalValue={obj?.customPrice} displayType="text" />,
                allowableUnitPrice: <CustomCurrencyField externalValue={obj?.allowableUnitPrice} displayType="text" />,
            };

            if (modifiedObj.priceType === "MAC") {
                const macName = macInfoList?.find((option) => option.macId === obj.macId)?.macName;
                modifiedObj["priceType"] = "Local Fee Schedule" + (macName ? " - " + macName : "");
            } else if (modifiedObj.priceType === "NATIONAL") {
                modifiedObj["priceType"] = "National";
            }

            if (!groupedData[groupKey]) {
                groupedData[groupKey] = [];
            }

            groupedData[groupKey].push(modifiedObj);
        });

        return groupedData;
    }, [productPricingHistory, macInfoList]);

    return (
        <Dialog
            open={open}
            keepMounted
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            fullScreen={isMobile}
            scroll="paper"
            PaperProps={{ sx: paperSx(isMobile) }}
            onClick={(event) => event.stopPropagation()}
            aria-describedby="sku-level-price-history-dialog"
            BackdropProps={{
                sx: {
                    backgroundColor: isDark ? "rgba(0,0,0,0.78)" : "rgba(15,23,42,0.5)",
                    backdropFilter: "blur(6px)",
                },
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    flex: 1,
                    minHeight: 0,
                    overflow: "hidden",
                }}
            >
                <Box sx={headerSx}>
                    <Box sx={iconBoxSx(tokens.accent)}>
                        <History size={20} color={tokens.accent} strokeWidth={1.8} />
                    </Box>
                    <Box sx={{ flex: 1, pr: 4 }}>
                        <Typography
                            fontWeight={700}
                            sx={{
                                fontSize: "1.1rem",
                                color: tokens.textPrimary,
                                lineHeight: 1.3,
                            }}
                        >
                            SKU Level Pricing History
                        </Typography>
                        <Typography
                            id="sku-level-price-history-dialog"
                            sx={{
                                fontSize: "0.8rem",
                                color: tokens.textSecondary,
                                mt: 0.15,
                            }}
                        >
                            View SKU-level list, allowable, and custom pricing changes over time.
                        </Typography>
                    </Box>
                    <IconButton onClick={onClose} size="small" sx={closeIconSx} aria-label="Close">
                        <CloseIcon sx={{ fontSize: "1rem" }} />
                    </IconButton>
                </Box>

                <Box
                    sx={{
                        ...bodySx,
                        minHeight: 0,
                        overflowY: "auto",
                        overflowX: "hidden",
                        scrollbarGutter: "stable",
                        "&::-webkit-scrollbar": { width: 6, height: 6 },
                        "&::-webkit-scrollbar-track": { background: "transparent" },
                        "&::-webkit-scrollbar-thumb": {
                            background: theme.palette.mode === "dark" ? "rgba(255,255,255,0.12)" : "#CBD5E1",
                            borderRadius: 4,
                        },
                    }}
                >
                    <Box sx={modalTableSx}>
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        {columnsPriceItem.map((column) => (
                                            <TableCell
                                                key={column.id}
                                                align={column.align || "left"}
                                            >
                                                {column.label}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {Object.keys(groupedRows).length === 0 ? (
                                        <TableRow>
                                            <TableCell
                                                colSpan={columnsPriceItem.length}
                                                sx={{ textAlign: "center", fontWeight: 600, py: 3 }}
                                            >
                                                No data available
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        Object.entries(groupedRows).map(([groupKey, rows]) => {
                                            const [effectiveDate, inactiveDate] = groupKey.split("-");
                                            return (
                                                <React.Fragment key={groupKey}>
                                                    <TableRow>
                                                        <TableCell
                                                            colSpan={columnsPriceItem.length}
                                                            className="sku-group-header"
                                                        >
                                                            Effective Date: {effectiveDate} | Inactive Date:{" "}
                                                            {inactiveDate}
                                                        </TableCell>
                                                    </TableRow>

                                                    {rows.map((row, rowIndex) => (
                                                        <TableRow key={rowIndex}>
                                                            {columnsPriceItem.map((column) => (
                                                                <TableCell
                                                                    key={column.id}
                                                                    align={column.align || "left"}
                                                                    className={
                                                                        column.id === "sku" ? "sku-cell" : undefined
                                                                    }
                                                                >
                                                                    {row[column.id]}
                                                                </TableCell>
                                                            ))}
                                                        </TableRow>
                                                    ))}
                                                </React.Fragment>
                                            );
                                        })
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                </Box>

                <Box sx={footerSx}>
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "flex-end",
                            width: "100%",
                        }}
                    >
                        <Button
                            type="button"
                            variant="outlined"
                            onClick={onClose}
                            startIcon={<Cancel sx={{ fontSize: "1rem !important" }} />}
                            sx={cancelBtnSx}
                        >
                            Cancel
                        </Button>
                    </Box>
                </Box>
            </Box>
        </Dialog>
    );
};

export default ProductItemSKULevelPriceHistoryPopup;
